import AppShell from '@/components/AppShell'
import DataTable, { Col } from '@/components/DataTable'
import EmptyState from '@/components/EmptyState'
import Skeleton from '@/components/Skeleton'
import { useEffect, useMemo, useState } from 'react'
import { useNotifications } from '@/lib/notifications'

// --- storage keys (scoped for this page) ---
const SK = {
  filters: 'loads:filters:v1',
  presets: 'loads:presets:v1',
  table:   'tbl:loads:v1',
} as const

// --- types ---
type Tri = 'any' | 'yes' | 'no'
type DateFor = 'Delivered' | 'Loaded'

type Filters = {
  scale?: string
  security?: string
  start?: string
  end?: string
  dateFor: DateFor

  loggingCompany?: string[]
  truckingCompany?: string[]
  loader?: string
  driver?: string
  vendor?: string
  job?: string

  product?: string
  destination?: string
  millPurchaser?: string
  customerPurchaser?: string
  status?: string

  reconciled: Tri
  invoiced: Tri

  groupBy?: string
  statementDate?: string
  grossWeight?: number
  weightUnits?: 'Tons'|'Cords'
  verificationStatus?: string
  originSite?: string
  destinationSite?: string

  showProfit?: boolean
}

type Preset = { id:string; name:string; filters: Filters }

// --- sample result row type (adjust to your real data later) ---
type LoadRow = {
  sec: string
  scale: string
  job: string
  product: string
  origin: string
  destination: string
  logger: string
  trucking: string
  netUnits: number
  loadedDate: string
  deliveredDate: string
  status: string
}

// tiny mock in-memory data
const MOCK: LoadRow[] = [
  { sec:'A1234', scale:'101', job:'Holly Hill Export Yard', product:'Pine Log 16"', origin:'Stand 12', destination:'Port of Chs', logger:'GreenCo', trucking:'Red Trucking', netUnits:27.48, loadedDate:'2025-09-01', deliveredDate:'2025-09-02', status:'Delivered' },
  { sec:'A1235', scale:'102', job:'New Holly Hill WY', product:'Pine Log 19’ 12"', origin:'Stand 8', destination:'Export Mill', logger:'GreenCo', trucking:'Red Trucking', netUnits:24.12, loadedDate:'2025-09-03', deliveredDate:'2025-09-03', status:'Entered' },
]

// helpers
const loadJSON = <T,>(k:string, d:T):T => {
  try { const v = localStorage.getItem(k); return v? JSON.parse(v) as T : d }
  catch { return d }
}
const saveJSON = (k:string, v:any) => localStorage.setItem(k, JSON.stringify(v))

const DEFAULT_FILTERS: Filters = {
  dateFor: 'Delivered',
  reconciled: 'any',
  invoiced: 'any',
  weightUnits: 'Tons',
  showProfit: false,
}

// ------------------------------------------------------------

export default function LoadsIndex(){
  const { push } = useNotifications()
  const [tab, setTab] = useState<'All'|'Entered'|'Delivered'|'Settled'|'Reconciled'|'Paper Entry'|'Import Mill Data'>('All')
  const [filters, setFilters] = useState<Filters>(loadJSON(SK.filters, DEFAULT_FILTERS))
  const [querying, setQuerying] = useState(false)
  const [rows, setRows] = useState<LoadRow[]>([])
  const [presets, setPresets] = useState<Preset[]>(loadJSON(SK.presets, [] as Preset[]))

  useEffect(()=>{ saveJSON(SK.filters, filters) }, [filters])
  useEffect(()=>{ saveJSON(SK.presets, presets) }, [presets])

  // fake query
  function search(){
    setQuerying(true)
    setTimeout(()=>{
      let out = [...MOCK]
      if (tab==='Entered') out = out.filter(r=>r.status==='Entered')
      if (tab==='Delivered') out = out.filter(r=>r.status==='Delivered')
      // simple examples using a couple of filters
      if (filters.job) out = out.filter(r => r.job.toLowerCase().includes(filters.job!.toLowerCase()))
      if (filters.product) out = out.filter(r => r.product.toLowerCase().includes(filters.product!.toLowerCase()))
      if (filters.start){
        out = out.filter(r => (filters.dateFor==='Delivered'? r.deliveredDate : r.loadedDate) >= filters.start!)
      }
      if (filters.end){
        out = out.filter(r => (filters.dateFor==='Delivered'? r.deliveredDate : r.loadedDate) <= filters.end!)
      }
      setRows(out)
      setQuerying(false)
    }, 350)
  }

  function clear(){
    setFilters(DEFAULT_FILTERS)
    setRows([])
  }

  // presets
  function savePreset(){
    const name = prompt('Preset name?')
    if (!name) return
    const p: Preset = { id: crypto.randomUUID(), name, filters }
    const next = [p, ...presets]
    setPresets(next)
    push({ title:'Saved preset', body:name })
  }
  function applyPreset(p:Preset){ setFilters(p.filters); push({title:'Applied preset', body:p.name}); }
  function removePreset(id:string){ setPresets(presets.filter(p=>p.id!==id)) }

  // table columns
  const cols: Col[] = [
    { key:'sec', label:'Sec#' },
    { key:'scale', label:'Scale#' },
    { key:'job', label:'Job' },
    { key:'product', label:'Product' },
    { key:'origin', label:'Origin' },
    { key:'destination', label:'Destination' },
    { key:'logger', label:'Logger' },
    { key:'trucking', label:'Trucking' },
    { key:'netUnits', label:'Net Units' },
    { key:'loadedDate', label:'Loaded Date' },
    { key:'deliveredDate', label:'Delivery Date' },
    { key:'status', label:'Status' },
  ]

  // quick tabs count (mock)
  const counts = useMemo(()=>{
    const all = MOCK.length
    return {
      All: all,
      Entered: MOCK.filter(r=>r.status==='Entered').length,
      Delivered: MOCK.filter(r=>r.status==='Delivered').length,
      Settled: 0,
      Reconciled: 0,
    }
  },[])

  // top action bar (tabs)
  const Tabs = (
    <div className="flex gap-6 mb-4">
      {(['All','Entered','Delivered','Settled','Reconciled','Paper Entry','Import Mill Data'] as const).map(t=>(
        <button
          key={t}
          className={`pb-2 border-b-2 -mb-px ${tab===t?'border-[var(--brand)] text-[var(--brand)]':'border-transparent text-[var(--muted)]'}`}
          onClick={()=>setTab(t)}
        >
          {t} {t!=='Paper Entry' && t!=='Import Mill Data' && <span className="text-xs text-[var(--muted)]">({(counts as any)[t] ?? 0})</span>}
        </button>
      ))}
    </div>
  )

  // handle special tabs
  if (tab==='Paper Entry'){
    return (
      <AppShell title="Loads">
        {Tabs}
        <div className="card">
          <div className="card-body">
            <div className="text-sm text-[var(--muted)]">
              Paper Entry form lives here. (If you already built a dedicated page, link it.)
            </div>
          </div>
        </div>
      </AppShell>
    )
  }
  if (tab==='Import Mill Data'){
    return (
      <AppShell title="Loads">
        {Tabs}
        <div className="card">
          <div className="card-header">Import Mill Data</div>
          <div className="card-body grid gap-3">
            <div className="text-sm text-[var(--muted)]">
              Use <strong>Import Templates</strong> to map CSV headers to fields, then upload here.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select className="select" defaultValue="">
                <option value="" disabled>Select Template</option>
                <option>Andrews Export – CSV</option>
              </select>
              <input className="input" type="file" />
            </div>
            <div><button className="btn">Import</button></div>
          </div>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell title="Loads">
      {Tabs}

      {/* FILTER BAR */}
      <div className="card mb-3">
        <div className="card-body grid gap-4">
          {/* row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-sm">Scale #</label>
              <input className="input" placeholder="1234, 1235 OR range 1234--1235, % for wildcard"
                     value={filters.scale||''}
                     onChange={e=>setFilters({...filters, scale:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Security Tag</label>
              <input className="input" placeholder="1234, 1235 OR range 1234--1235, % for wildcard"
                     value={filters.security||''}
                     onChange={e=>setFilters({...filters, security:e.target.value})}/>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-sm">Start Date</label>
                <input className="input" type="date" value={filters.start||''} onChange={e=>setFilters({...filters,start:e.target.value})}/>
              </div>
              <div>
                <label className="text-sm">End Date</label>
                <input className="input" type="date" value={filters.end||''} onChange={e=>setFilters({...filters,end:e.target.value})}/>
              </div>
              <div>
                <label className="text-sm">* Date For</label>
                <select className="select" value={filters.dateFor}
                        onChange={e=>setFilters({...filters, dateFor:e.target.value as DateFor})}>
                  <option>Delivered</option>
                  <option>Loaded</option>
                </select>
              </div>
            </div>
          </div>

          {/* row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-sm">Logging Company</label>
              <input className="input" placeholder="Select Some Options" value={(filters.loggingCompany||[]).join(', ')}
                     onChange={e=>setFilters({...filters, loggingCompany: e.target.value? e.target.value.split(',').map(s=>s.trim()): []})}/>
            </div>
            <div>
              <label className="text-sm">Trucking Company</label>
              <input className="input" placeholder="Select Some Options" value={(filters.truckingCompany||[]).join(', ')}
                     onChange={e=>setFilters({...filters, truckingCompany: e.target.value? e.target.value.split(',').map(s=>s.trim()): []})}/>
            </div>
            <div>
              <label className="text-sm">Loader</label>
              <input className="input" placeholder="Select an Option" value={filters.loader||''}
                     onChange={e=>setFilters({...filters, loader:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Driver</label>
              <input className="input" placeholder="Select an Option" value={filters.driver||''}
                     onChange={e=>setFilters({...filters, driver:e.target.value})}/>
            </div>
          </div>

          {/* row 3 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-sm">Vendor</label>
              <input className="input" placeholder="Select Some Options" value={filters.vendor||''}
                     onChange={e=>setFilters({...filters, vendor:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Job</label>
              <input className="input" placeholder="Select an Option" value={filters.job||''}
                     onChange={e=>setFilters({...filters, job:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Product</label>
              <input className="input" placeholder="Select an Option" value={filters.product||''}
                     onChange={e=>setFilters({...filters, product:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Destination</label>
              <input className="input" placeholder="Select an Option" value={filters.destination||''}
                     onChange={e=>setFilters({...filters, destination:e.target.value})}/>
            </div>
          </div>

          {/* row 4 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-sm">Mill Purchaser</label>
              <input className="input" placeholder="Select an Option" value={filters.millPurchaser||''}
                     onChange={e=>setFilters({...filters, millPurchaser:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Customer Purchaser</label>
              <input className="input" placeholder="Select an Option" value={filters.customerPurchaser||''}
                     onChange={e=>setFilters({...filters, customerPurchaser:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Status</label>
              <input className="input" placeholder="Select Some Options" value={filters.status||''}
                     onChange={e=>setFilters({...filters, status:e.target.value})}/>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-sm">Reconciled</label>
                <select className="select" value={filters.reconciled} onChange={e=>setFilters({...filters,reconciled:e.target.value as Tri})}>
                  <option value="any">Any</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="text-sm">Invoiced</label>
                <select className="select" value={filters.invoiced} onChange={e=>setFilters({...filters,invoiced:e.target.value as Tri})}>
                  <option value="any">Any</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="text-sm">Statement Date</label>
                <input className="input" type="date" value={filters.statementDate||''}
                       onChange={e=>setFilters({...filters, statementDate:e.target.value})}/>
              </div>
            </div>
          </div>

          {/* row 5 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="grid grid-cols-3 gap-2 items-end">
              <div className="col-span-2">
                <label className="text-sm">Gross Weight</label>
                <input className="input" type="number" step="0.001" value={filters.grossWeight ?? ''}
                       onChange={e=>setFilters({...filters, grossWeight: e.target.value? Number(e.target.value) : undefined})}/>
              </div>
              <div>
                <label className="text-sm">&nbsp;</label>
                <select className="select" value={filters.weightUnits} onChange={e=>setFilters({...filters, weightUnits: e.target.value as Filters['weightUnits']})}>
                  <option>Tons</option><option>Cords</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm">Verification Status</label>
              <input className="input" placeholder="Select Some Options" value={filters.verificationStatus||''}
                     onChange={e=>setFilters({...filters, verificationStatus:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Origin Site</label>
              <input className="input" placeholder="Select Some Options" value={filters.originSite||''}
                     onChange={e=>setFilters({...filters, originSite:e.target.value})}/>
            </div>
            <div>
              <label className="text-sm">Destination Site</label>
              <input className="input" placeholder="Select Some Options" value={filters.destinationSite||''}
                     onChange={e=>setFilters({...filters, destinationSite:e.target.value})}/>
            </div>
          </div>

          {/* bottom bar */}
          <div className="flex items-center gap-3">
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={!!filters.showProfit} onChange={e=>setFilters({...filters, showProfit: e.target.checked})}/>
              Show Profit?
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button className="btn" onClick={search}>Search</button>
            <button className="btn-secondary" onClick={clear}>Clear</button>
            <button className="btn-secondary" onClick={()=>window.print()}>Print</button>
            <button className="btn-secondary" onClick={()=>push({title:'Print Load Book', body:'(stub)'})}>Print Load Book</button>
            <button className="btn-secondary" onClick={()=>push({title:'Export .xlsx', body:'(stub — use CSV for now)'})}>Export to .xlsx</button>
            <button className="btn-secondary" onClick={()=>push({title:'Rayonier CFX Export', body:'(stub)'})}>Rayonier CFX Export</button>
            <button className="btn-secondary" onClick={()=>push({title:'Export to FPA', body:'(stub)'})}>Export to FPA</button>

            <div className="ml-auto flex gap-2">
              <button className="btn-secondary" onClick={savePreset}>Save as preset</button>
            </div>
          </div>
        </div>
      </div>

      {/* results table */}
      {querying ? (
        <Skeleton className="h-48" />
      ) : rows.length === 0 ? (
        <EmptyState title="Please enter a search option or choose from one of our suggested searches below.">
          <div className="mt-3">
            <div className="font-semibold mb-1">Suggested Reports</div>
            <ul className="list-disc pl-5 text-sm text-[var(--muted)]">
              <li><button className="underline" onClick={()=>{ setTab('Delivered'); search() }}>Loads Loaded Today</button></li>
              <li><button className="underline" onClick={()=>{ setTab('All'); search() }}>All Entered Loads</button></li>
            </ul>
          </div>
        </EmptyState>
      ) : (
        <DataTable columns={cols} rows={rows} filename="loads.csv" storageKey={SK.table} />
      )}

      {/* custom reports / presets */}
      <div className="mt-6">
        <div className="text-[var(--muted)] text-sm mb-1">Custom Reports</div>
        {presets.length===0 ? (
          <div className="text-sm text-[var(--muted)]">None yet. Use <strong>Save as preset</strong> after you set filters.</div>
        ):(
          <div className="grid gap-2">
            {presets.map(p=>(
              <div key={p.id} className="flex items-center justify-between border border-[color:var(--border)] rounded-xl px-3 py-2">
                <div className="font-medium">{p.name}</div>
                <div className="flex gap-2">
                  <button className="btn-secondary" onClick={()=>applyPreset(p)}>Apply</button>
                  <button className="btn-secondary" onClick={()=>removePreset(p.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  )
}
